import React from 'react';

import { GenericReducer, GenericReducerFunctionMap } from 'contexts/GenericReducer';

import axios from 'axios';

import { useConfigFunctions, useConfigState } from '../Config';

import Loading from 'components/Loading';

// #region Contexts

const contentContext = React.createContext();
function useAuthContext() {
  const context = React.useContext(contentContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

const dispatchContext = React.createContext();
function useAuthDispatchContext() {
  const context = React.useContext(dispatchContext);
  if (context === undefined) {
    throw new Error('AuthDispatchContext must be used within an AuthProvider');
  }
  return context;
}

function getApiEndpoint() {
  return `https://${useConfigFunctions().getEndpoint()}/api`;
}

export function postAPI(path, body, requestConfig) {
  // Determine proper endpoint
  const endpoint = getApiEndpoint();

  // Build call structure
  if (!requestConfig) {
    requestConfig = {};
  }
  requestConfig.headers = {
    accept: 'application/json',
  };

  return axios.post(endpoint + path, body, requestConfig);
}

function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
}

function clearSession(context, dispatch) {
  const update_map = {
    session: '',
  };
  GenericReducerFunctionMap(dispatch).UpdateContextStateMap(update_map);
}

function setEmail(context, dispatch, email) {
  const update_map = {
    email: email,
  };
  GenericReducerFunctionMap(dispatch).UpdateContextStateMap(update_map);
}

function updateAuthState(context, dispatch, accessToken, idToken, refreshToken) {
  //  update the context
  GenericReducerFunctionMap(dispatch).UpdateContextStateMap({
    session: '',
    accessToken: accessToken,
    idToken: idToken,
    refreshToken: refreshToken,
    verifying: false,
    authenticated: true,
    idTokenClaims: parseJwt(idToken),
  });
  // update the local storage
  localStorage.setItem('AccessToken', accessToken);
  localStorage.setItem('IdToken', idToken);
  localStorage.setItem('RefreshToken', refreshToken);
}

function doneVerifying(context, dispatch) {
  GenericReducerFunctionMap(dispatch).UpdateContextStateMap({ verifying: false });
}

function LogAuthSettings(context) {
  console.log('context', context);
}

function Logout(context, dispatch) {
  clearSession(context, dispatch);
  localStorage.removeItem('AccessToken');
  localStorage.removeItem('IdToken');
  localStorage.removeItem('RefreshToken');
  GenericReducerFunctionMap(dispatch).UpdateContextStateMap({
    accessToken: '',
    idToken: '',
    refreshToken: '',
    idTokenClaims: {},
    authenticated: false,
  });
}

function acquireToken(context, dispatch) {
  return refreshFederatedTokens(context, dispatch)
    .then((response) => {
      // The refresh function already updated the auth state, but update the claims again
      const claims = parseJwt(response.id_token);
      GenericReducerFunctionMap(dispatch).UpdateContextStateMap({ 
        idTokenClaims: claims,
        verifying: false,
        authenticated: true,
      });
      return response;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}


function getToken(context, dispatch) {
  return new Promise((resolve, reject) => {
    if (isAuthenticated(context)) {
      resolve(context.accessToken);
    } else {
      acquireToken(context, dispatch).then((response) => {
        resolve(response.accessToken);
      }).catch((e) => {
        // console.log('Get token error', e);
        resolve('');
      });
    }
  });
}

function isAuthenticated(context) {
  if (context.accessToken === undefined) { return false; }
  if (context.accessToken === '') { return false; }
  if (context.idTokenClaims.exp < Date.now() / 1000) { return false; }
  return true;
}

function AuthenticatedCall(context, dispatch, url, method, body) {
  return new Promise((resolve, reject) => {
    getToken(context, dispatch).then((token) => {
      const headers = {
        auth: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };
      const options = {
        method,
        headers,
      };
      if (body !== undefined) {
        options.body = JSON.stringify(body);
      }
      if (url.startsWith('/')) {
        url = `${context.config.getApiEndpoint()}${url}`;
      }
      axios.post(url, body, options)
        .then((response) => {
          try {
            const contentType = response.headers.get('Content-Type');
            if (contentType === null) {
              resolve(response.json());
            } else if (contentType.includes('application/json')) {
              // console.log(response)
              try {
                resolve(response.json());
              } catch (error) {
                try {
                  resolve(response.data);
                } catch (error) {
                  console.log('unable to parse response 1', error);
                  console.log(response)
                  reject(error);
                }
              }
              resolve(response.json());
            } else {
              resolve(response.blob());
            }
          } catch (error) {
            // console.log('unable to parse response 2', error);
            reject(error);
          }
        })
        .catch((error) => {
          reject(error);
        });
    })
      .catch((error) => {
        if (error.message === 'Unauthorised') {
          return Login(context);
        }
        reject(error);
      });
  });
}

function federatedLogin(context, dispatch) {
  console.log('federatedLogin');
  const auth_config = context.config.getAuthConfig();
  console.log('auth_config', auth_config);
  const COGNITO_DOMAIN = auth_config.oauth.domain;
  const CLIENT_ID = auth_config.userPoolWebClientId;
  const REDIRECT_URI = auth_config.oauth.redirectSignIn;
  const RESPONSE_TYPE = auth_config.oauth.responseType;
  const SCOPES = auth_config.oauth.scope.join('+');
  const state = 'state'; // TODO: Generate a random state value
  const prompt = 'select_account';
  const url = `https://${COGNITO_DOMAIN}/oauth2/authorize?response_type=${RESPONSE_TYPE}&client_id=${CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&scope=${SCOPES}`;
  // const url = `https://${COGNITO_DOMAIN}/oauth2/authorize?response_type=${RESPONSE_TYPE}&amp;client_id=${CLIENT_ID}&amp;redirect_uri=${encodeURIComponent(REDIRECT_URI)}&amp;scope=${SCOPES}&amp;state=${encodeURIComponent(state)}&amp;identity_provider=${identityProvider}&amp;prompt=${prompt}`;
  // const url = `https://${COGNITO_DOMAIN}/oauth2/authorize?response_type=${RESPONSE_TYPE}&amp;client_id=${CLIENT_ID}&amp;redirect_uri=${encodeURIComponent(REDIRECT_URI)}&amp;scope=${SCOPES}&amp;identity_provider=EMAIL_OTP`
  // const url = `https://${COGNITO_DOMAIN}/oauth2/authorize?response_type=${RESPONSE_TYPE}&client_id=${CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&scope=${SCOPES}&identity_provider=EMAIL_OTP`;

  console.log('url', url);
  window.location.href = url;
}

function getFederatedTokens(context, dispatch, code) {
  const auth_config = context.config.getAuthConfig();
  const tokenUrl = `https://${auth_config.oauth.domain}/oauth2/token`;
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: auth_config.userPoolWebClientId,
    redirect_uri: auth_config.oauth.redirectSignIn,
    code,
  });
  return axios.post(tokenUrl, body, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  })
    .then((response) => {
      localStorage.setItem('AccessToken', response.data.access_token);
      localStorage.setItem('IdToken', response.data.id_token);
      localStorage.setItem('RefreshToken', response.data.refresh_token);
      return response.data;
    })
    .catch((error) => {
      console.log('error', error);
    });
}

function refreshFederatedTokens(context, dispatch) {
  // console.log('refreshFederatedTokens');
  const auth_config = context.config.getAuthConfig();
  const tokenUrl = `https://${auth_config.oauth.domain}/oauth2/token`;
  // Prefer the refresh token from localStorage (or context) if available
  const refreshToken = localStorage.getItem('RefreshToken') || context.refreshToken;
  if (!refreshToken) {
    // console.error("No refresh token available");
    return Promise.reject(new Error("No refresh token available"));
  }

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: auth_config.userPoolWebClientId,
    refresh_token: refreshToken,
  });

  return axios.post(tokenUrl, body, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  })
    .then((response) => {
      // Cognito returns new tokens (access_token, id_token)
      const newAccessToken = response.data.access_token;
      const newIdToken = response.data.id_token;
      // Call updateAuthState so the context and localStorage are both updated.
      updateAuthState(context, dispatch, newAccessToken, newIdToken, refreshToken);
      return response.data;
    })
    .catch((error) => {
      console.error('error refreshing tokens', error);
      return Promise.reject(error);
    });
}




// #endregion

export function useAuthFunctions() {
  const context = useAuthContext();
  const dispatch = useAuthDispatchContext();
  const authFunctions = {
    logSettings: () => LogAuthSettings(context, dispatch),
    logout: () => Logout(context, dispatch),
    isAuthenticated: () => isAuthenticated(context, dispatch),
    authenticatedCall: (url, method, body) => AuthenticatedCall(context, dispatch, url, method, body),
    getToken: () => getToken(context, dispatch),
    doneVerifying: () => doneVerifying(context, dispatch),
    getEndpoint: () => useConfigFunctions().getEndpoint(),
    federatedLogin: () => federatedLogin(context, dispatch),
    getFederatedTokens: (code) => getFederatedTokens(context, dispatch, code),
    refreshFederatedTokens: () => refreshFederatedTokens(context, dispatch),
  };
  return authFunctions;
}

export function useAuthState() {
  const context = useAuthContext();
  return context;
}

export function AuthProvider({ children, ...rest }) {
  const config = useConfigFunctions()
  const [configuration, setConfiguration] = React.useState(null);
  const [loaded, setLoaded] = React.useState(false);

  const [authContextState, authContextDispatch] = React.useReducer(GenericReducer, {
    ...configuration,
    config: config,
    authenticated: false,
    verifying: true,
    user: {},
    network_error: false,
  });

  React.useEffect(() => {
    refreshFederatedTokens(authContextState, authContextDispatch)
      .then((result) => {
        setLoaded(true);
      })
      .catch((e) => {
        setLoaded(true);
      });

  }, []);

  return loaded ?
    (
      <contentContext.Provider value={authContextState}>
        <dispatchContext.Provider value={authContextDispatch}>
          {children}
        </dispatchContext.Provider>
      </contentContext.Provider>
    ) : (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100vw',
        height: '100vh',
        backgroundColor: '#f0f0f0', // Optional: Set a background color
      }}>
        <Loading message="Authenticating..." />
      </div>
    );
}
